import styled from '@emotion/styled'
import { Accordion, Icon } from '@mobi/component-library/Common/V2'
import { LocalConstants } from '@mobi/component-library/Common/V2/Accordion/Accordion'
import { colors, font, spacing } from '@mobi/component-library/Theme/Common'
import React, { useEffect } from 'react'
import { MFADeviceItem } from './MFADeviceItem'
import { mfaDevicesQueryKey, useMfaDevices } from '../Hooks/useMfaDevices'
import { Spinner } from '@mobi/component-library/Common/Spinner'
import { PasswordVerificationTray } from './PasswordVerificationTray'
import { usePasswordVerificationTray } from '../Hooks/usePasswordVerificationTray'
import { useTrustDevice } from '../Hooks/useTrustDevice'
import { forgetDevice } from '@core/Areas/Login/helpers/api'
import { clearTrustedDeviceDetails } from '@core/Areas/Login/Store'
import { addToast } from '@mobi/component-library/Feedback/Toast'
import { useDispatch } from 'react-redux'
import { useQueryClient } from '@tanstack/react-query'
import { trackMfaEvent } from '../Helpers/helpers'

export const MFADeviceGroup: React.FC<{ accountNumber: number }> = ({ accountNumber }) => {
  const { trustDevice } = useTrustDevice()
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const [isAccordionOpen, setIsAccordionOpen] = React.useState(false)
  const { data, isLoading, error, refetch } = useMfaDevices(accountNumber, isAccordionOpen)
  const devices = data?.devices || []
  const currentDeviceKey = data?.currentDeviceKey
  const accountIdentifier = accountNumber.toString()

  const { isPasswordTrayOpen, closeVerifyPasswordTray, handleCognitoError, handleVerify } =
    usePasswordVerificationTray()

  useEffect(() => {
    if (error) {
      setIsAccordionOpen(false)
      handleCognitoError(error, () => {
        refetch()
        setIsAccordionOpen(true)
      })
    }
  }, [error])

  async function handleToggle(wasExpanded: boolean) {
    setIsAccordionOpen(!wasExpanded)
    trackMfaEvent({ interaction_type: 'Manage trusted devices', otpFlow: 'Settings' })
  }

  const onTrustDevice = async () => {
    if (accountNumber) {
      await trustDevice(accountNumber)
    }
  }

  const onForgetDevice = async (deviceKey: string) => {
    if (accountNumber) {
      try {
        await forgetDevice({ deviceKey })

        if (deviceKey === currentDeviceKey) {
          dispatch(
            clearTrustedDeviceDetails({
              accountIdentifier,
            })
          )
        }

        // Invalidate and refetch the query
        await queryClient.invalidateQueries({ queryKey: mfaDevicesQueryKey(accountNumber) })

        addToast({
          id: 'forget-device-success',
          title: 'Device removed',
          subtitle:
            'This device is no longer trusted and will require verification codes for future logins',
          icon: 'success',
        })

        trackMfaEvent({ interaction_type: 'Remove trusted device', otpFlow: 'Settings' })
      } catch {
        addToast({
          id: 'forget-device-error',
          isDismissable: true,
          timeoutMs: Infinity,
          title: 'Unable to remove device',
          subtitle: 'Something went wrong. Please try again.',
          icon: 'error',
        })
      }
    }
  }

  return (
    <>
      {isLoading ? <Spinner /> : null}
      <DeviceGroupStyled>
        {accountNumber ? (
          <PasswordVerificationTray
            isOpen={isPasswordTrayOpen}
            onVerify={handleVerify}
            onCancel={closeVerifyPasswordTray}
            accountNumber={accountNumber}
          />
        ) : null}
        <Accordion
          title={<DeviceGroupHeader />}
          onToggle={handleToggle}
          isExpandedControlled={isAccordionOpen}
        >
          {devices.length > 0 ? (
            devices.map(device => (
              <MFADeviceItem
                key={device.deviceKey}
                device={device}
                onDelete={onForgetDevice}
                onTrustDevice={onTrustDevice}
              />
            ))
          ) : error ? (
            <p>There was an error fetching your trusted devices</p>
          ) : (
            <NoDevicesStyled>
              <p>No trusted devices registered. Devices can be added during the login process.</p>
            </NoDevicesStyled>
          )}
        </Accordion>
      </DeviceGroupStyled>
    </>
  )
}

export const DeviceGroupHeader: React.FC = () => {
  return (
    <DeviceGroupHeaderStyled>
      <Icon name='SolidCheckVerified02' color={colors.studio[500]} />
      <div>
        <h1>Manage trusted devices</h1>
        <p>Register your trusted devices for quicker, secure access.</p>
      </div>
    </DeviceGroupHeaderStyled>
  )
}

export const DeviceGroupStyled = styled.div({
  marginTop: spacing.sm,

  // Override Accordion style
  [`&& .${LocalConstants.DefaultThemeClass}`]: {
    backgroundColor: colors.surface[100],
    borderRadius: '0.75rem',
    border: `0.5px solid ${colors.neutral[200]}`,
    boxShadow: 'none',
  },
})

export const DeviceGroupHeaderStyled = styled.div({
  backgroundColor: colors.surface[100],
  display: 'flex',
  gap: spacing.smx1,
  alignItems: 'center',
  alignSelf: 'stretch',
  margin: '1rem 0',

  '> div': { margin: `0 ${spacing.smx1}` },
  h1: {
    fontFamily: font.family.primary,
    ...font.size.md,
    fontWeight: font.weight.semibold,
    margin: '0 0 0.25rem 0',
  },
  p: {
    color: colors.neutral[800],
    alignSelf: 'stretch',
    fontFamily: font.family.primary,
    fontWeight: font.weight.regular,
    ...font.size.base,
    margin: 0,
  },
})

export const NoDevicesStyled = styled.div({
  padding: spacing.sm,
  textAlign: 'center',
  color: colors.neutral[600],

  '& p': {
    margin: 0,
    fontFamily: font.family.primary,
    ...font.size.sm,
  },
})
